import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Menu, MapPin, Activity, DollarSign, FileText, Shield, QrCode, X } from 'lucide-react';
import NavButton from './NavButton';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';

import qr from '../assets/sedna dashboard QR.png';
import formQr from '../assets/Sedna Form Qr.png';
import driLogoLight from '../assets/sedna_logo.png';
import driLogoDark from '../assets/sedna_logo_dark.png';
import driPoweredLogo from '../assets/DRi_logo.png';

export const navItems = [
  { to: '/equipment-location', icon: MapPin, label: 'Equipment Location' },
  { to: '/equipment-status', icon: Activity, label: 'Equipment Status' },
  { to: '/finance', icon: DollarSign, label: 'Finance' },
  { to: '/log-details', icon: FileText, label: 'Log Details' },
  { to: '/admin-dashboard', icon: Shield, label: 'Admin', adminOnly: true },
];

const SIDEBAR_STORAGE_KEY = 'sidebar-collapsed';

export default function Sidebar() {
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'true';
  });
  const [activeQr, setActiveQr] = useState(null);
  const [mounted, setMounted] = useState(false);
  const [compactScreen, setCompactScreen] = useState(false);

  const { theme } = useTheme();
  const { user } = useAuth();

  const isLight = theme === 'light';
  const topLogo = isLight ? driLogoDark : driLogoLight;

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (collapsed) {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, 'true');
    } else {
      localStorage.removeItem(SIDEBAR_STORAGE_KEY);
    }
  }, [collapsed]);

  useEffect(() => {
    const query = window.matchMedia('(max-width: 1366px)');
    const updateCompact = (event) => setCompactScreen(event.matches);

    updateCompact(query);
    query.addEventListener('change', updateCompact);
    return () => query.removeEventListener('change', updateCompact);
  }, []);

  useEffect(() => {
    window.isViewingQr = Boolean(activeQr);

    if (!activeQr) {
      return () => {
        window.isViewingQr = false;
      };
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setActiveQr(null);
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.isViewingQr = false;
    };
  }, [activeQr]);

  const expandedWidth = compactScreen ? 216 : 248;
  const collapsedWidth = compactScreen ? 80 : 88;

  const containerClasses = `${
    isLight
      ? 'border-slate-200/60 bg-white/85 text-slate-900 shadow-xl shadow-slate-200/70'
      : 'border-white/10 bg-slate-950/80 text-slate-100 shadow-2xl shadow-slate-900/60'
  } hidden md:flex h-screen max-h-screen flex-col overflow-hidden border-r backdrop-blur-2xl transition-colors md:sticky md:top-0 md:self-start`;

  const navPanelClasses = collapsed
    ? 'flex flex-col items-center gap-1.5 rounded-3xl border border-white/10 bg-white/5 p-2 backdrop-blur-xl'
    : 'flex flex-col gap-1.5 rounded-3xl border border-white/10 bg-white/5 p-2.5 backdrop-blur-xl';

  const sectionBorderClass = isLight ? 'border-slate-200/60' : 'border-white/10';

  const qrCards = [
    { id: 'form', label: 'QR Form', image: formQr },
    { id: 'dashboard', label: 'Dash QR', image: qr },
  ];

  return (
    <>
      <motion.aside
        animate={{ width: collapsed ? collapsedWidth : expandedWidth }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className={containerClasses}
        aria-label="Primary sidebar"
      >
        {/* Header */}
        <div className={`relative flex h-24 items-center justify-center border-b px-4 ${sectionBorderClass}`}>
          {!collapsed && (
            <img src={topLogo} alt="Digi Rock Innovations logo" className="h-10 w-auto" />
          )}
          <button
            className={`absolute right-4 flex h-10 w-10 items-center justify-center rounded-2xl border ${
              isLight
                ? 'border-slate-200/80 bg-white/70'
                : 'border-white/10 bg-white/5'
            }`}
            onClick={() => setCollapsed((p) => !p)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <Menu className="h-5 w-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className={`${collapsed ? 'px-2.5 py-5' : 'px-4 py-5'} flex-1 overflow-y-auto`}>
          <div className={navPanelClasses}>
            {navItems
              .filter((item) => !(item.adminOnly && (!user || user.visitor)))
              .map((item) => (
                <NavButton key={item.to} {...item} collapsed={collapsed} />
              ))}
          </div>
        </nav>

        {/* Bottom section */}
        {!collapsed && (
          <div className="space-y-4 px-5 pb-8 pt-2">
            {/* QR buttons */}
            <div className="rounded-2xl border border-white/10 bg-white/5 p-3 backdrop-blur-xl">
              <div className="flex gap-2">
                {qrCards.map((card) => (
                  <button
                    key={card.id}
                    onClick={() => setActiveQr(card)}
                    className="flex flex-1 flex-col items-center gap-2 rounded-xl border border-white/10 bg-white/10 px-3 py-2 text-xs font-semibold"
                  >
                    <span className="flex h-10 w-10 items-center justify-center rounded-lg">
                      <QrCode className="h-4 w-4" />
                    </span>
                    {card.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Powered by */}
            <a
              href="https://digirockinnovations.com"
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center gap-1 text-center opacity-80 transition hover:opacity-100"
            >
              <span className="text-[10px] italic tracking-wide text-slate-500 dark:text-slate-400">
                Powered by
              </span>
              <img
                src={driPoweredLogo}
                alt="Digi Rock Innovations"
                className="h-6 w-auto"
              />
            </a>
          </div>
        )}
      </motion.aside>

      {/* QR Modal */}
      {mounted &&
        activeQr &&
        createPortal(
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/85 p-6">
            <div className="relative w-full max-w-xl rounded-4xl border border-white/10 bg-slate-950/90 p-8 text-center">
              <button
                onClick={() => setActiveQr(null)}
                className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full border border-white/10"
              >
                <X className="h-5 w-5" />
              </button>
              <img
                src={activeQr.image}
                alt={activeQr.label}
                className="mx-auto w-full max-w-[18rem] rounded-3xl bg-white p-5"
              />
              <h3 className="mt-4 text-lg font-semibold text-white">
                {activeQr.label}
              </h3>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
